import { API_URL } from '../config';
import { useNotifications } from '../contexts/NotificationContext';

// Lightweight event sink for non-React contexts
async function postError(payload: any) {
  try {
    await fetch(`${API_URL}/logs/error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
  } catch {}
}

type Level = 'error' | 'warn' | 'info' | 'debug';

export const makeLogger = (notify: (msg: string) => void) => {
  const emit = async (level: Level, message: string, context?: unknown) => {
    if (process.env.NODE_ENV === 'development') {
      // Keep console in dev for diagnostics
      // eslint-disable-next-line no-console
      (console as any)[level]?.(message, context ?? '');
    } else {
      if (level === 'debug' || level === 'info') {
        // suppress noisy logs in prod
      }
    }

    if (level === 'error' || level === 'warn') {
      // Show concise banner to user
      notify(message);
      // Send structured details to backend
      const stack = (context as any)?.stack || (context instanceof Error ? context.stack : undefined);
      const route = typeof window !== 'undefined' ? window.location.pathname : undefined;
      await postError({ level, message, stack, route, context });
    }
  };

  return {
    error: (message: string, context?: unknown) => emit('error', message, context),
    warn: (message: string, context?: unknown) => emit('warn', message, context),
    info: (message: string, context?: unknown) => emit('info', message, context),
    debug: (message: string, context?: unknown) => emit('debug', message, context),
  };
};

// Hook to obtain a logger bound to the Notification system
export const useLogger = () => {
  const { notify } = useNotifications();
  const notifyFn = (msg: string) => notify({ message: msg, variant: 'error' });
  return makeLogger(notifyFn);
};

// Default logger instance for non-React code paths
export const defaultLogger = (() => {
  const notify = (msg: string) => {
    try {
      const event = new CustomEvent('tpc-notify', { detail: { message: msg, variant: 'error' } });
      window.dispatchEvent(event);
    } catch {}
  };
  return makeLogger(notify);
})();


